import ReactQuill from 'react-quill'
import React, {useState, forwardRef, useRef, useEffect} from 'react'
import {Controller, FormProvider, useForm} from 'react-hook-form'
import DatePicker from 'react-datepicker'
import {useSelector} from 'react-redux'
import toast from "react-hot-toast";
import Select from 'react-select'
import {useNavigate, useParams} from "react-router-dom"
import {useAppDispatch, RootState} from '@/store'
import {getCalendar} from '@/store/calendar/action'
import {getVenueDetail} from '@/store/venue-detail/action'
import Button from '@/components/common/Button'
import HeaderLayout from '@/components/common/CustomLayout/HeaderLayout'
import Calendar from '@/components/common/Calendar'
import Input from '@/components/common/Input'
import FileInput from '@/components/common/FileInput'
import CalendarWeekly from '@/components/common/CalendarWeekly'
import {ReactComponent as RiArrowLeft} from '@/assets/icons/arrow-left.svg'
import {ReactComponent as RiArrowRight} from '@/assets/icons/arrow-right.svg'
import {
  ReactComponent as RiDeleteBinLine
} from '@/assets/icons/ri-delete-bin-line.svg'
import {
  ProgramStatus,
  ProgramStatuses,
  ProgramType,
} from '@/pages/Programs/List/constant'
import './index.css'
import 'react-quill/dist/quill.snow.css'
import 'react-datepicker/dist/react-datepicker.css'
import {getUserVenues} from "@/store/user-info/action"
import {uploadFiles} from "@/store/file/action"
import {acceptedFileTypes, MAX_FILE_SIZE} from "@/config/app"
import {
  createProgram,
  editProgram,
  getProgramDetail
} from "@/store/program-detail/action"
import {deleteProgram} from "@/store/program-summary/action";
import {addDays} from "@/utils";
import {programDetailActions} from "@/store/program-detail/slice";
import {venueDetailActions} from "@/store/venue-detail/slice";
import {GENERAL_STATUS_STYLE} from "@/constant/status";
import { openingHours } from '@/utils/validation'

const OPENING_HOURS = openingHours;

const modules = {
  toolbar: [
    [{header: [1, 2, false]}],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [
      {list: 'ordered'},
      {list: 'bullet'},
      {indent: '-1'},
      {indent: '+1'},
    ],
    ['link', 'image'],
    ['clean'],
  ],
}

const dateUpdate = (operator: string, startDate: number) => {
  let newStart = startDate
  if (operator === 'prev') {
    newStart += -7 * 24 * 60 * 60 * 1000
  } else if (operator === 'next') {
    newStart += 7 * 24 * 60 * 60 * 1000
  }
  return {
    newStart: new Date(newStart).getTime(),
    newEnd: new Date(newStart + 6 * 24 * 60 * 60 * 1000).getTime(),
  }
}

const ShowCalendar = forwardRef<any>(
  (
    {
      value,
      onClick,
    }: {
      value: string
      onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
    },
    ref
  ) => (
    <Button
      variant="basic"
      type="button"
      className="border border-grey-line-subtle w-full"
      onClick={onClick}
      ref={ref}
    >
      {value}
    </Button>
  )
)

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
]

const getSelectValue = (venues, field) => {
  const found = venues?.find(p => p.id === field.value)

  return found ? {
    value: found.id,
    label: found.name
  } : null
}

const emptyProgramState = {
  title: '',
  price: null,
  quota: null,
  status: ProgramStatus.DRAFT,
  type: ProgramType.ONE_TIME,
  content: '',
  thumbnails: [],
  venue_id: '',
  details: []
}

const validateProgram = (p: Program) => {
  let errorMessage = ''
  if (!p.title) {
    errorMessage = 'Program title can not be empty'
  } else if (!p.price || p.price <= 0) {
    errorMessage = 'Price must be filled'
  } else if (!p.quota || p.quota <= 0) {
    errorMessage = 'Quota must be filled'
  } else if (!p.type) {
    errorMessage = 'Program type must be selected'
  } else if (!p.content) {
    errorMessage = 'Program content can not be empty'
  } else if (!p.thumbnails.length) {
    errorMessage = 'Program thumbnail image can not be empty'
  } else if (!p.venue_id) {
    errorMessage = 'Venue must be selected'
  } else if (!p.details.length) {
    errorMessage = 'Program schedules must be selected'
  }

  if (errorMessage) {
    toast.error(errorMessage)
  }
  return !errorMessage
}

const ProgramDetail = () => {
  const navigate = useNavigate()
  const {programId} = useParams()
  const initialStartDate =
    new Date().getDate() - ((new Date().getDay() + 6) % 7)
  const programDetailMethods = useForm<Program>({
    defaultValues: emptyProgramState
  })
  const {
    getValues,
    watch,
    setValue,
    reset,
    control,
    register,
  } = programDetailMethods
  const initialDate = {
    start: new Date().setDate(initialStartDate),
    end: addDays(new Date().getTime(), 7),
    selected: new Date().setHours(0, 0, 0, 0),
  }
  const existingProgramData = useSelector((state: RootState) =>
    state.programDetail.state)
  const {state: calendarItems} = useSelector((state: RootState) =>
    state.calendar)
  const venueState = useSelector((state: RootState) =>
    state.userInfo.state.venues)
  const venueDetailState = useSelector((state: RootState) =>
    state.venueDetail.venue)

  const [selectedSchedules, setSelectedSchedules] = useState({})
  const [selectedDay, setSelectedDay] = useState(0)
  const [selectedDate, setSelectedDate] = useState(initialDate.selected)
  const [startDate, setStartDate] = useState(initialDate.start)
  const [endDate, setEndDate] = useState(initialDate.end)
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null)
  const [bookingByCourt, setBookingByCourt] = useState([])
  const inputRef = useRef()
  const dispatch = useAppDispatch()
  const programType = watch('type')
  const thumbnails = watch('thumbnails')
  const programStatus = watch('status')
  const reactiveVenueId = watch('venue_id')

  const getScheduleFromDetails = (programDetails: ProgramDetails[], courts: Court[]) => {
    const summary = {}
    programDetails.forEach(detail => {
      const convertedDate = detail.start_date
      const { code, id } = courts.find(court => court.id === detail.venue_court_id) ?? {}
      const schedule = {
        ...detail,
        court: code ? { code, id } : null,
        hour: detail.start_time,
        selectedDate: convertedDate
      }
      const timeboxTime = programType === 'ONE_TIME' ? convertedDate : detail.day
      if (summary[timeboxTime]) {
        summary[timeboxTime].push(schedule)
      } else {
        summary[timeboxTime] = [schedule]
      }
    })
    return summary
  }

  const setSchedulesFromProgram = () => {
    if (!existingProgramData || !selectedVenue) {
      return
    }
    const { details } = existingProgramData
    const { courts } = selectedVenue

    if (!courts.length) {
      return
    }

    const existingSchedules = getScheduleFromDetails(details, courts)
    setSelectedSchedules(existingSchedules)
  }

  const getCleanProgramData = (p) => ({
    ...p,
    price: parseInt(p.price, 10),
    quota: parseInt(p.quota, 10)
  })

  const onSuccessAction = () => {
    navigate('/dashboard/programs')
  }

  const onSubmit = () => {
    const details = Object.entries(selectedSchedules).map((schedule: {}) =>
      schedule[1].map((bookingDetail) => {
        const {
          court,
          hour,
          selectedDate: date,
          selectedDay: day,
        } = bookingDetail

        const dateToPass = date

        const detail: {
          day?: number
          start_time: number
          end_time: number
          start_date?: number
          end_date?: number
          venue_court_id: string
        } = {
          venue_court_id: court.id,
          start_time: hour,
          end_time: hour + 1,
        }
        if (programType === 'ONE_TIME') {
          detail.day = 0
          detail.start_date = dateToPass
          detail.end_date = dateToPass
        } else {
          detail.day = day
          detail.start_date = startDate
          detail.end_date = endDate
        }
        return detail
      })
    )
    const rawProgramData = {
      ...programDetailMethods.getValues(),
      details: details.flat(),
    }
    const programPayload = getCleanProgramData(rawProgramData)

    if (!validateProgram(programPayload)) {
      return
    }

    if (programId) {
      dispatch(editProgram(programPayload))
        .then(onSuccessAction)
    } else {
      dispatch(createProgram(programPayload))
        .then(onSuccessAction)
    }
  }

  const deleteFile = () => {
    setValue('thumbnails', [])
  }

  const setFileFromResponse = (response) => {
    const {payload} = response
    setValue('thumbnails', [payload.url])
  }

  const handleChangeFile = (fileInput?: File) => {
    if (fileInput && fileInput.size < MAX_FILE_SIZE) {
      const formData = new FormData()
      formData.append('file', fileInput)
      dispatch(uploadFiles({formData})).then(setFileFromResponse)
    }
  }

  const handleDeleteProgram = () => {
    dispatch(deleteProgram(programId))
      .then(onSuccessAction)
  }

  const getProgramDataIfEdit = () => {
    if (!programId) {
      reset(emptyProgramState)
      return
    }
    dispatch(getProgramDetail(programId))
      .then(setSchedulesFromProgram)
  }

  useEffect(() => {
    setSelectedVenue(null)
    dispatch(programDetailActions.resetProgramDetail())
    dispatch(venueDetailActions.resetVenueDetail())
    dispatch(getUserVenues({}))
    getProgramDataIfEdit()
  }, [])
  useEffect(() => {
    if (!reactiveVenueId) {
      return
    }
    dispatch(getVenueDetail(reactiveVenueId))
  }, [reactiveVenueId])
  useEffect(() => {
    if (!programId || !existingProgramData?.id) {
      return
    }
    reset(existingProgramData)
  }, [existingProgramData])
  useEffect(() => {
    if (!reactiveVenueId) {
      return
    }
    (async () => {
      await dispatch(
        getCalendar({
          dateMeta: selectedDate,
          venueMeta: reactiveVenueId,
        })
      )
    })()
  }, [selectedVenue, selectedDate, programType, reactiveVenueId])
  useEffect(() => {
    setBookingByCourt([])
    const bookings = []
    calendarItems.forEach((calendarItem) => {
      const schedules = calendarItem.items?.map((item) => ({
        hour: item.start_time || 0,
        type: item.event_type || '',
        detail: item.description || '',
      }))
      bookings.push({
        code: calendarItem.court_code,
        id: calendarItem.court_id,
        schedules,
      })
    })
    setBookingByCourt(bookings)
  }, [calendarItems])
  useEffect(() => {
    (async () => {
      await setSelectedVenue({...venueDetailState})
    })()
    setSelectedSchedules({})
  }, [venueDetailState])
  useEffect(() => {
    setSchedulesFromProgram()
  }, [existingProgramData, selectedVenue])

  return (
    <FormProvider {...programDetailMethods}>
      <HeaderLayout
        title={programId ? 'Edit Program' : 'Write New'}
        rightComponent={
          <div className="flex justify-center items-center gap-4">
            {
              programId ?
                <>
                  <Button
                    onClick={handleDeleteProgram}
                    type={null}
                    variant="secondary"
                    className="border-stroke-subtle border px-4"
                  >
                    <div className="flex gap-2 justify-center items-center h-4">
                      <RiDeleteBinLine/>
                    </div>
                  </Button>
                  <div className="separator"/>
                </>
                : <></>
            }
            <select
              className={`color-select ${GENERAL_STATUS_STYLE[programStatus]}`}
              {...register('status')}
            >
              {
                ProgramStatuses.map(i =>
                  <option
                    key={i}
                    value={i}
                    className={GENERAL_STATUS_STYLE[i]}>
                    {i.replace('_', ' ')}
                  </option>
                )
              }
            </select>
            <Button
              onClick={onSubmit}
              type="submit"
              variant="primary"
              className="whitespace-nowrap flex gap-2 px-12 py-2 justify-center items-center text-jsm font-medium"
            >
              Submit
            </Button>
          </div>
        }
      >
        <div className="program-detail-wrapper">
          <div className="flex flex-col w-full justify-start items-start gap-6">
            <div className="program-detail-card">
              <Input
                className="text-jb-3xl p-7"
                expanded
                name="title"
                type="text"
                placeholder="Enter program title ..."
              />
            </div>
            <div className="program-detail-card">
              <Controller
                control={control}
                render={({field}) =>
                  <ReactQuill
                    value={field.value}
                    onChange={field.onChange}
                    formats={formats}
                    modules={modules}
                  />}
                name="content"
              />
            </div>
            <div className="program-detail-card">
              <div
                className="flex w-full justify-between border-b-stroke-subtle border-b">
                <div
                  className="flex items-center justify-between py-6 px-7 w-full text-jlg font-bold p-7">
                  <p>Program Recurrence</p>
                  <div
                    className="flex rounded border border-grey-line-subtle w-3/5">
                    <Controller
                      name="type"
                      control={control}
                      render={({field}) => <>
                        <div className="flex w-full h-full items-center">
                          <input
                            {...field}
                            type="radio"
                            id="oneTime"
                            value="ONE_TIME"
                            className="peer hidden"
                            checked={field.value === 'ONE_TIME'}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                              setSelectedSchedules({})
                            }}
                          />
                          <label
                            htmlFor="oneTime"
                            className="flex items-center justify-center cursor-pointer select-none w-full h-full peer-checked:bg-blue-light peer-checked:text-white"
                          >
                            One Time
                          </label>
                        </div>
                        <div className="flex w-full h-full items-center">
                          <input
                            {...field}
                            type="radio"
                            id="recurring"
                            value="RECURRING"
                            className="peer hidden"
                            checked={field.value === 'RECURRING'}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                              setSelectedSchedules({})
                            }}
                          />
                          <label
                            htmlFor="recurring"
                            className="flex items-center justify-center cursor-pointer select-none w-full h-full peer-checked:bg-blue-light peer-checked:text-white"
                          >
                            Recurring
                          </label>
                        </div>
                      </>}/>
                  </div>
                </div>
              </div>
              {programType === 'ONE_TIME' && selectedVenue?.id && (
                <div>
                  <div
                    className="flex justify-end items-center w-full py-6 px-8 gap-12">
                    <div className="flex gap-2">
                      <Button
                        variant="basic"
                        type="button"
                        className="border border-grey-line-subtle"
                        onClick={() => {
                          const newDates = dateUpdate('', new Date().getTime())
                          setStartDate(newDates.newStart)
                          setEndDate(newDates.newEnd)
                        }}
                      >
                        Today
                      </Button>
                      <Button
                        variant="basic"
                        type="button"
                        className="border border-grey-line-subtle"
                        onClick={() => {
                          const newDates = dateUpdate('prev', startDate)
                          setStartDate(newDates.newStart)
                          setEndDate(newDates.newEnd)
                        }}
                      >
                        <RiArrowLeft/>
                      </Button>
                      <DatePicker
                        selectsRange
                        startDate={startDate}
                        endDate={endDate}
                        onChange={(date) => {
                          const {newStart, newEnd} = dateUpdate(
                            '',
                            date[0].getTime()
                          )
                          setStartDate(newStart)
                          setEndDate(newEnd)
                        }}
                        customInput={<ShowCalendar ref={inputRef}/>}
                      />
                      <Button
                        variant="basic"
                        type="button"
                        className="border border-grey-line-subtle"
                        onClick={() => {
                          const newDates = dateUpdate('next', startDate)
                          setStartDate(newDates.newStart)
                          setEndDate(newDates.newEnd)
                        }}
                      >
                        <RiArrowRight/>
                      </Button>
                    </div>
                  </div>
                  <Calendar
                    startDate={startDate}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    hours={OPENING_HOURS}
                    courts={selectedVenue?.courts || []}
                    bookingByCourt={bookingByCourt}
                    selectedSchedules={selectedSchedules}
                    setSelectedSchedules={setSelectedSchedules}
                  />
                </div>
              )}
              {programType === 'RECURRING' && selectedVenue?.id && (
                <div>
                  <div
                    className="flex items-center w-full py-6 px-8 gap-12 border-b-stroke-subtle border-b">
                    <div className="flex w-full gap-7">
                      <div className="flex items-center w-full gap-5">
                        <p className="font-bold text-lg whitespace-nowrap">
                          Start Date
                        </p>
                        <DatePicker
                          selected={new Date(startDate)}
                          onChange={(date) => {
                            const newStart = date.getTime()
                            setStartDate(newStart)
                          }}
                          customInput={<ShowCalendar ref={inputRef}/>}
                        />
                      </div>
                      <div className="flex items-center w-full gap-5">
                        <p className="font-bold text-lg whitespace-nowrap">
                          End Date
                        </p>
                        <DatePicker
                          selected={new Date(endDate)}
                          onChange={(date) => {
                            const newEnd = date.getTime()
                            setEndDate(newEnd)
                          }}
                          customInput={<ShowCalendar ref={inputRef}/>}
                        />
                      </div>
                    </div>
                  </div>
                  <CalendarWeekly
                    hours={OPENING_HOURS}
                    courts={selectedVenue?.courts || []}
                    selectedDay={selectedDay}
                    selectedSchedules={selectedSchedules}
                    setSelectedDay={setSelectedDay}
                    setSelectedSchedules={setSelectedSchedules}
                    prices={selectedVenue?.prices || []}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="program-detail-card h-fit">
            <div className="program-info-wrapper">
              <FileInput
                classNames="h-[200px] w-full"
                setFile={handleChangeFile}
                accept={acceptedFileTypes}
              />
              {thumbnails?.length ? (
                <div className="flex gap-2 mt-2">
                  <div className="rounded overflow-hidden">
                    <img
                      src={getValues('thumbnails.0')}
                      alt=""
                      className="h-[50px] w-[100px] object-cover"
                    />
                  </div>
                  <div className="flex flex-grow justify-between items-center">
                    <button
                      onClick={() => deleteFile()}
                      type="button"
                      className="text-red-primary"
                    >
                      <RiDeleteBinLine/>
                    </button>
                  </div>
                </div>
              ) : (
                ''
              )}
            </div>
            <div className="horizontal-separator"/>
            <div className="program-info-wrapper">
              <div className="program-info-title">Venue</div>
              <div className="program-info-sub-title">
                Which venue is this news published?
              </div>
              <Controller
                control={control}
                render={({field}) =>
                  <Select
                    {...field}
                    className="w-full"
                    placeholder="Select venue"
                    onChange={(val) => {
                      field.onChange(val.value)
                    }}
                    options={venueState.map((venue) => ({
                      label: venue.name,
                      value: venue.id,
                    }))}
                    value={getSelectValue(venueState, field)}
                  />}
                name="venue_id"/>
            </div>
            <div className="horizontal-separator"/>
            <div className="program-info-wrapper">
              <div className="program-info-title">Price</div>
              <div className="program-info-sub-title">
                Set the price for this program
              </div>
              <div className="custom-input">
                <div className="custom-input-left">Rp</div>
                <Input
                  className="rounded-none"
                  expanded
                  name="price"
                  type="number"
                  placeholder="e.g 1250000"
                />
                <div className="custom-input-right">/package</div>
              </div>
            </div>
            <div className="horizontal-separator"/>
            <div className="program-info-wrapper">
              <div className="program-info-title">Quota</div>
              <div className="program-info-sub-title">
                Set the maximum quota of participants
              </div>
              <div className="custom-input">
                <Input
                  className="rounded-none rounded-bl rounded-tl"
                  expanded
                  name="quota"
                  type="number"
                  placeholder="e.g 100"
                />
                <div className="custom-input-right">/participants</div>
              </div>
            </div>
          </div>
        </div>
      </HeaderLayout>
    </FormProvider>
  )
}

export default ProgramDetail
